import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import videojs from 'video.js';

import { embedUrl } from "./Helper";
import { getIcon } from "../../../helpers/icons";
import { generateMediaUrl } from "../../../helpers/media";
import Link from "../../../components/Link";
import Center from "../../../components/Center";
import Section from "../../../components/Section";
import DialogBox from "../../../components/DialogBox";
import Feedback from "../../../components/DialogBox/Feedback";

import 'video.js/dist/video-js.css';
import style from "./style.module.css";

export default function Player(props) {
    const {
        name = "",
        media_type = "",
        content_type = "",
        video_file = "",
        video_link = "",
        audio_file = "",
        center = false,
        additional_resource_article = [],
        backButton = true,
        transcript = "",
        transcript_download = 0,
        preview_image = "",
        author_name = "",
        audio_poster = false,
    } = props;

    const navigate = useNavigate();
    const { winHeight } = useOutletContext();
    const videoNode = useRef(null);
    const playerRef = useRef(null);
    const [popup, setPopup] = useState(false);
    const [currentArticleIndex, setCurrentArticleIndex] = useState(0);
    const isInvalidMedia = ["audio", "photo", "presentation", "document"].includes(media_type) && !audio_file;
    let poster = generateMediaUrl(preview_image, "2880x1620");
    let autoPlay = true;

    let getPath = "";
    let posterHeight = true;
    if (media_type === "video" && content_type === "file" && video_file.id !== undefined && video_file.id !== "") {
        getPath = video_file.id;
    }
    else if (["audio"].includes(media_type) && audio_file?.id) {
        getPath = audio_file.id;
    }
    else if (["presentation", "document", "photo"].includes(media_type) && audio_file?.id) {
        getPath = audio_file.id;
        posterHeight = false;
        poster = false;
        autoPlay = false;
    }
    const setPath = getPath !== "" ? process.env.REACT_APP_CDN_URL + "/playlist/" + getPath : "";
    const nextArticle = () => {
        const startingArticleIndex = (additional_resource_article || []) ? additional_resource_article.findIndex(article => article.url) : "";
        if (currentArticleIndex === -1 || currentArticleIndex === 0) {
            setCurrentArticleIndex(startingArticleIndex);
            navigate(`/article/${additional_resource_article[startingArticleIndex].url}`);
            return;
        }
        const nextIndex = currentArticleIndex + 1;
        if (nextIndex < (additional_resource_article || []).length) {
            setCurrentArticleIndex(nextIndex);
            navigate(`/article/${additional_resource_article[nextIndex].url}`);
        }
    };

    let fileType = "";
    if (media_type === "video") {
        fileType = content_type;
    }
    else if (media_type === "audio" || media_type === "photo" || media_type === "presentation" || media_type === "document") {
        fileType = "file";
    }

    useEffect(() => {
        if (isInvalidMedia) return;
        if (fileType === "file" && setPath !== "") {
            if (videoNode.current) {
                const isAudio = media_type === "audio" || media_type === "photo" || media_type === "presentation" || media_type === "document";
                const player = videojs(videoNode.current, {
                    playbackRates: [0.5, 1, 1.5, 2],
                    controls: true,
                    preload: 'auto',
                    responsive: true,
                    autoplay: autoPlay,
                    muted: isAudio ? false : true,
                    poster: isAudio ? poster : false,
                    audioPosterMode: isAudio ? audio_poster : false,
                    fluid: true,
                    sources: [
                        {
                            src: setPath,
                            type: 'application/x-mpegURL',
                        },
                    ],
                    controlBar: {
                        remainingTimeDisplay: true,
                        skipButtons: {
                            forward: 10,
                            backward: 10
                        },
                    },
                });

                const CustomConrolbarButton = videojs.getComponent('Button');
                const hasNextArticle = currentArticleIndex + 1 <= (additional_resource_article || []).length;
                if (hasNextArticle) {
                    class NextArticle extends CustomConrolbarButton {
                        constructor(player, options) {
                            super(player, options);
                            this.addClass('vjs-next-link-button');
                            this.controlText('Next Article');
                        }
                        handleClick() {
                            nextArticle();
                        }
                    }
                    videojs.registerComponent('NextArticle', NextArticle);
                    player.getChild('controlBar').addChild('NextArticle', {}, 4);
                }

                if (transcript) {
                    class TypeScript extends CustomConrolbarButton {
                        constructor(player, options) {
                            super(player, options);
                            this.addClass('vjs-typescript-button');
                            this.controlText('TypeScript');
                        }
                        handleClick() {
                            setPopup("transcript");
                        }
                    }
                    videojs.registerComponent('TypeScript', TypeScript);
                    player.getChild('controlBar').addChild('TypeScript', {}, 6);
                }

                const CustomControl = videojs.getComponent('Component');
                class VideoTitle extends CustomControl {
                    constructor(player, options) {
                        super(player, options);
                        this.addClass('vjs-video-title');
                        const titleElement = document.createElement('div');
                        titleElement.textContent = name;
                        titleElement.className = 'vjs-title-text';
                        this.el().appendChild(titleElement);
                    }
                }

                videojs.registerComponent('VideoTitle', VideoTitle);
                player.getChild('controlBar').addChild('VideoTitle', {}, 4);

                if (media_type === "audio") {
                    const posterElement = player.el().querySelector('.vjs-poster');
                    if (posterElement) {
                        const articleName = document.createElement('div');
                        articleName.className = 'vjs-poster-title';
                        articleName.textContent = name;
                        const authorName = document.createElement('div');
                        authorName.className = 'vjs-poster-author-name';
                        authorName.textContent = author_name;
                        const soundWave = document.createElement('div');
                        soundWave.className = `${style.soundWave}`;
                        soundWave.innerHTML = "<span></span><span></span><span></span><span></span><span></span><i></i>";
                        posterElement.appendChild(articleName);
                        posterElement.appendChild(authorName);
                        posterElement.appendChild(soundWave);

                    }
                }

                playerRef.current = player;
                return () => {
                    if (player) {
                        player.dispose();
                    }
                };
            }
        }
        // eslint-disable-next-line
    }, [content_type, setPath, currentArticleIndex, additional_resource_article, transcript, media_type]);

    if (isInvalidMedia) {
        return null;
    }

    let url = "";
    if (media_type === "video" && content_type === "link") {
        url = embedUrl(video_link);
    }

    return (
        <>
            <Section id="player" center={center} className={`${style.Player} ${style[media_type + fileType]}`}>
                {
                    ((backButton === true) && (media_type === "audio" || media_type === "video")) && (
                        <Center className={style.backButtonWrap}>
                            <Link
                                onClick={() => { navigate(-1) }}
                                className={`${style.backButton}`}
                            >
                                <i className={getIcon("Arrow-left")}></i>
                            </Link>
                        </Center>
                    )
                }
                <div className={`${style.playerWrap} ${posterHeight === false ? style.staticHeight : ''} ${posterHeight === false ? `staticHeight` : ''}`} style={posterHeight ? { height: `${winHeight}px` } : undefined}>
                    {
                        (media_type === "video" && content_type === "link" && url !== "") && (
                            <div className={style.iframe}>
                                <div className={style.iframeWrap}>
                                    <div className={style.playerContainer}>
                                        <iframe src={url} frameBorder='0' allowFullScreen title='WW2HQ Player' />
                                    </div>
                                </div>
                            </div>
                        )
                    }
                    {
                        (media_type === "video" && content_type === "file" && setPath !== "") && (
                            <div className={style.videoPlayer}>
                                <div data-vjs-player>
                                    <video ref={videoNode} className="video-js vjs-default-skin"></video>
                                </div>
                            </div>
                        )
                    }
                    {
                        ((media_type === "audio" || media_type === "photo" || media_type === "presentation" || media_type === "document") && setPath !== "") && (
                            <div className={style.videoPlayer}>
                                <div data-vjs-player>
                                    <audio ref={videoNode} className="video-js vjs-default-skin"></audio>
                                </div>
                            </div>
                        )
                    }
                </div>
            </Section>
            {
                popup === "transcript" && (
                    <DialogBox
                        onClose={() => setPopup(false)}
                        className={`${style.articlePopup} ${style.transcriptPopup}`}
                        title={false}
                        headerClass={style.dialogBoxHeader}
                        modalClassName={style.articleModal}
                        buttonClassName={style.customCloseStyle}
                        render={() => {
                            return (
                                <Feedback name={name} transcript={transcript} download={transcript_download} />
                            )
                        }}
                    />
                )
            }
        </>
    )
}